import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowRightLeft, LogIn, LogOut } from 'lucide-react';
import { useToast } from '@/components/common/Toast';
import { useAuth } from '@/components/Auth/AuthProvider';
import ums, { assignableRoles, COMPANY_ADMIN_ROLE } from '@/services/ums.service';

const button = 'inline-flex items-center gap-1 px-3 py-1.5 border border-gn-light rounded hover:bg-gn-tertiary disabled:opacity-50 text-sm';
const primary = 'inline-flex items-center gap-1 px-3 py-1.5 bg-gn-blue text-gn-inverse rounded text-sm disabled:opacity-50';

/**
 * The platform's staff in a customer's company (Veramenta docs/en/62): join it at once - no invitation -
 * with the roles chosen here, then switch to it and work there like one of its employees: open and save
 * workspaces, be shared with, run analyses. Ours take none of the company's seats, and its administrator
 * sees them in the list of people, marked as ours. Leaving takes every role there away again.
 */
const StaffMembership: React.FC<{ tenantId: string; companyName: string }> = ({ tenantId, companyName }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { chooseCompany } = useAuth();
  const [choosing, setChoosing] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);

  const me = useQuery({ queryKey: ['umsMe'], queryFn: () => ums.me(), retry: false, staleTime: 10 * 60_000 });
  const membersKey = ['umsMembers', tenantId];
  const members = useQuery({ queryKey: membersKey, queryFn: () => ums.members(tenantId), retry: false });
  const roles = useQuery({ queryKey: ['umsRoles', tenantId], queryFn: () => ums.roles(tenantId), retry: false });
  const offered = useMemo(() => assignableRoles(roles.data ?? []), [roles.data]);

  const mine = (members.data ?? []).find((m) => m.principalId === me.data?.principalId && m.state !== 'REVOKED');
  const member = !!mine && mine.state === 'ACTIVE';

  // By default: the company's administrator and GeoNexora's own administrator - everything an employee can do.
  useEffect(() => {
    if (offered.length && picked.length === 0) {
      setPicked(offered.filter((r) => r.key === COMPANY_ADMIN_ROLE || (r.product === 'geonexora' && r.key === 'admin')).map((r) => r.id));
    }
  }, [offered]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => queryClient.invalidateQueries({ queryKey: membersKey });
  const failed = (e: unknown) => addToast(e instanceof Error ? e.message : String(e), 'error');

  const join = useMutation({
    mutationFn: () => ums.joinAsStaff(tenantId, picked),
    onSuccess: () => {
      setChoosing(false);
      refresh();
      addToast(t('staff.joined', { company: companyName }), 'success');
    },
    onError: failed,
  });
  const leave = useMutation({
    mutationFn: () => ums.removeMember(tenantId, me.data!.principalId),
    onSuccess: () => {
      refresh();
      addToast(t('staff.left', { company: companyName }), 'success');
    },
    onError: failed,
  });
  const go = async () => {
    await chooseCompany(tenantId);
    navigate('/');
  };

  if (me.isError || members.isError) {
    return null;
  }
  const roleName = (key: string, fallback: string) => t(`access.role.${key}`, { defaultValue: fallback });

  return (
    <div className="border border-gn-light rounded p-3 mb-4 bg-gn-surface" data-testid="staff-membership">
      {member ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm">
            {t('staff.member', { company: companyName })}
            {' '}
            <span className="text-gn-muted">
              ({mine!.assignments.filter((a) => a.scopeId === tenantId).map((a) => roleName(a.roleKey, a.roleName)).join(', ') || t('staff.no_roles')})
            </span>
          </span>
          <div className="flex-1" />
          <button type="button" className={primary} onClick={() => void go()} data-testid="staff-switch">
            <ArrowRightLeft size={14} /> {t('staff.switch')}
          </button>
          <button type="button" className={button} disabled={leave.isPending} data-testid="staff-leave"
                  onClick={() => { if (window.confirm(t('staff.leave_confirm', { company: companyName }))) leave.mutate(); }}>
            <LogOut size={14} /> {t('staff.leave')}
          </button>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm">{t('staff.not_member', { company: companyName })}</span>
            <div className="flex-1" />
            {!choosing && (
              <button type="button" className={primary} onClick={() => setChoosing(true)} data-testid="staff-join-open">
                <LogIn size={14} /> {t('staff.join')}
              </button>
            )}
          </div>
          {choosing && (
            <div className="mt-3">
              <p className="text-sm mb-1">{t('staff.roles')}</p>
              <div className="flex flex-wrap gap-3 mb-2">
                {offered.map((r) => (
                  <label key={r.id} className="inline-flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={picked.includes(r.id)} data-testid={`staff-role-${r.key}`}
                           onChange={(e) => setPicked(e.target.checked ? [...picked, r.id] : picked.filter((x) => x !== r.id))} />
                    {roleName(r.key, r.name)}
                  </label>
                ))}
              </div>
              <p className="text-xs text-gn-muted mb-3">{t('staff.hint')}</p>
              <div className="flex gap-2">
                <button type="button" className={primary} disabled={picked.length === 0 || join.isPending}
                        onClick={() => join.mutate()} data-testid="staff-join">
                  <LogIn size={14} /> {t('staff.join_now')}
                </button>
                <button type="button" className={button} onClick={() => setChoosing(false)}>{t('buttons.cancel')}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StaffMembership;
